import { logEvent } from 'firebase/analytics';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { analyticsInstanceSelector, callSelector, subscribedGameIdSelector, subscriptionEpochSelector } from '../services/GameSlice';
import { emitCommand, getSocket } from '../socket';
import CallSession, { autoJoinTarget } from './CallSession';

const NOBODY = [];

const IDLE = {
    status: 'idle',
    askingPermission: false,
    error: null,
    notice: null,
    localStream: null,
    hasAudio: false,
    hasVideo: false,
    audioOn: false,
    videoOn: false,
    peers: []
};

/**
 * The video call for one game, for as long as the component using it is on
 * screen. `eligible` is whether this player may be in the call at all (a
 * seated human, with someone else human to talk to).
 */
const useCall = (gameId, eligible) => {
    const analytics = useSelector(analyticsInstanceSelector);
    const call = useSelector(callSelector);
    const subscribedGameId = useSelector(subscribedGameIdSelector);
    const epoch = useSelector(subscriptionEpochSelector);
    const [state, setState] = useState(IDLE);
    const sessionRef = useRef(null);
    const analyticsRef = useRef(analytics);
    analyticsRef.current = analytics;

    // One session per game. Changing games (a rematch) hangs up the old call.
    useEffect(() => {
        if (!gameId) {
            return undefined;
        }
        const session = new CallSession({
            gameId,
            socket: getSocket(),
            command: emitCommand,
            onChange: setState,
            onEvent: (name, params) => {
                if (analyticsRef.current) {
                    logEvent(analyticsRef.current, name, { gameId, ...params });
                }
            }
        });
        sessionRef.current = session;
        setState(session.snapshot());
        return () => {
            session.destroy();
            sessionRef.current = null;
            setState(IDLE);
        };
    }, [gameId]);

    const participants = call?.gameId === gameId ? call.participants : NOBODY;
    useEffect(() => {
        sessionRef.current?.syncParticipants(participants);
    }, [participants]);

    // Join by ourselves after a reload or a dropped connection, or when a
    // rematch carried us over from the last game's call. At most one attempt
    // per subscription, so a call that has since filled up is not retried in
    // a loop.
    const attemptedFor = useRef(null);
    const subscribed = Boolean(gameId) && subscribedGameId === gameId;
    useEffect(() => {
        const session = sessionRef.current;
        if (!session || !subscribed || !eligible || attemptedFor.current === epoch) {
            return;
        }
        const wanted = session.status === 'reconnecting' || (session.status === 'idle' && autoJoinTarget() === gameId);
        if (wanted) {
            attemptedFor.current = epoch;
            session.join();
        }
    }, [subscribed, eligible, epoch, gameId, state.status]);

    // Out of the call's reach: the window got too narrow for the board, or
    // the seat went to a computer. Hang up, but come back by ourselves if it
    // becomes possible again.
    useEffect(() => {
        const session = sessionRef.current;
        if (session && !eligible && session.status !== 'idle') {
            session.leave({ rejoinLater: true });
            attemptedFor.current = null;
        }
    }, [eligible]);

    const join = useCallback(() => sessionRef.current?.join(), []);
    const leave = useCallback(() => sessionRef.current?.leave(), []);
    const toggleAudio = useCallback(() => sessionRef.current?.toggleAudio(), []);
    const toggleVideo = useCallback(() => sessionRef.current?.toggleVideo(), []);

    return {
        ...state,
        enabled: Boolean(call?.enabled) && call?.gameId === gameId,
        // Whether to offer the call to this player at all.
        available: Boolean(call?.enabled) && call?.gameId === gameId && Boolean(eligible),
        max: call?.max || 5,
        participants,
        subscribed,
        join,
        leave,
        toggleAudio,
        toggleVideo
    };
};

export default useCall;
